import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePadelCenter } from "@/context/PadelCenterContext";
import { Badge } from "@/components/ui/badge";
import { Users, Buildings, Trophy, Sword, CalendarCheck, TrendUp, Broadcast, Medal, CalendarBlank, ChartBar, Sparkle, Pencil, Sneaker, TShirt, Tag } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import UserAvatar from "@/components/UserAvatar";
import HumanoidAvatar from "@/components/HumanoidAvatar";
import ScopeStats from "@/components/ScopeStats";
import MySummaryPanel from "@/components/MySummaryPanel";
import PlayerKpiRow from "@/components/PlayerKpiRow";
import AvatarStudio from "@/components/AvatarStudio";
import DashboardHero from "@/components/DashboardHero";
import DashboardShowcase from "@/components/DashboardShowcase";
import OrganizerBadge from "@/components/OrganizerBadge";
import { useLang } from "@/context/LanguageContext";
import { getPalaBrand, getApparelBrand, getShoesBrand } from "@/lib/avatar";
import { Lock, ShieldCheck } from "@phosphor-icons/react";
import { Link as RouterLink } from "react-router-dom";
import { PadelCenterBadge } from "@/components/PadelCenterLogo";
import ReferralCard from "@/components/ReferralCard";
import UpcomingBracketMatches from "@/components/UpcomingBracketMatches";
import OnboardingTour from "@/components/OnboardingTour";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEEK_LABELS = ["LUN", "MAR", "MIE", "JUE", "VIE", "SAB", "DOM"];

const statusColors = {
  en_curso: "bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/30",
  pendiente_validacion: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  validado: "bg-green-500/20 text-green-400 border-green-500/30",
  finalizado: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};
const statusLabel = { en_curso: "EN VIVO", pendiente_validacion: "POR VALIDAR", validado: "VALIDADO", finalizado: "TERMINADO" };

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const { pc } = usePadelCenter();
  const [stats, setStats] = useState(null);
  const [liveMatches, setLiveMatches] = useState([]);
  const [recentValidated, setRecentValidated] = useState([]);
  const [recentRetas, setRecentRetas] = useState([]);
  const [upcomingTournaments, setUpcomingTournaments] = useState([]);
  const [upcomingRetas, setUpcomingRetas] = useState([]);
  const [upcomingRentals, setUpcomingRentals] = useState([]);
  const [scoreboardRanking, setScoreboardRanking] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);

  // Show onboarding tour on first login if user has not seen it yet
  useEffect(() => {
    if (user && user.onboarding_seen === false) {
      const t = setTimeout(() => setTourOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [user]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    Promise.all([
      api.get("/statistics/dashboard"),
      api.get("/matches?status=en_curso&limit=5"),
      api.get("/matches?status=validado&limit=5"),
      api.get("/retas"),
      api.get("/tournaments"),
      api.get("/court-rentals"),
      api.get("/statistics/all-players"),
      user?.id ? api.get(`/users/${user.id}`) : Promise.resolve({ data: null }),
    ]).then(([s, live, val, retas, tours, rentals, apl, meRes]) => {
      setStats(s.data);
      setLiveMatches(live.data || []);
      setRecentValidated(val.data || []);
      const rs = retas.data || [];
      setRecentRetas(rs.filter((r) => r.status === "completada").slice(0, 5));
      setUpcomingRetas(rs.filter((r) => ["pendiente", "aceptada"].includes(r.status) && (!r.date || r.date >= today)).slice(0, 5));
      const ts = tours.data || [];
      setUpcomingTournaments(ts.filter((t) => ["abierto", "en_curso"].includes(t.status) && (!t.start_date || t.start_date >= today)).slice(0, 5));
      const rts = rentals.data || [];
      setUpcomingRentals(rts.filter((r) => r.status !== "cancelada" && (!r.date || r.date >= today)).slice(0, 5));
      setScoreboardRanking((apl.data || []).slice(0, 7));
      setMe(meRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.id]);

  const cards = [
    { label: "Jugadores", value: stats?.total_players || 0, icon: Users, color: "#CCFF00", to: "/dashboard/estadisticas" },
    { label: "Clubes", value: stats?.total_clubs || 0, icon: Buildings, color: "#007AFF", to: "/dashboard/clubes" },
    { label: "Torneos", value: stats?.total_tournaments || 0, icon: Trophy, color: "#FFB800", to: "/dashboard/torneos" },
    { label: "Retas Activas", value: stats?.active_retas || 0, icon: Sword, color: "#FF3B30", to: "/dashboard/retas" },
    { label: "Rentas", value: stats?.total_rentals || 0, icon: CalendarCheck, color: "#34C759", to: "/dashboard/canchas" },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  const renderMatchRow = (m) => {
    const s1 = m.sets.filter((s) => s.winner === 1).length;
    const s2 = m.sets.filter((s) => s.winner === 2).length;
    const setScores = m.sets.map((s) => `${s.pair1_games}-${s.pair2_games}${s.tiebreak ? ` (${s.tiebreak.pair1}-${s.tiebreak.pair2})` : ""}`).join(" · ");
    return (
      <Link to={`/dashboard/marcador/${m.id}`} key={m.id} className="flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 px-2 transition-colors" data-testid={`dashboard-match-${m.id}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="outline" className={`badge-status ${statusColors[m.status]} text-[10px]`}>{statusLabel[m.status] || m.status}</Badge>
            {m.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px]">Cat. {m.category}</Badge>}
            <span className="text-[10px] text-gray-600 font-['JetBrains_Mono']">{m.date}{m.time ? ` · ${m.time}` : ""}</span>
          </div>
          <p className="text-sm text-white truncate">
            <span className={m.winner_pair === 1 ? "text-[#CCFF00] font-semibold" : ""}>{m.pair1.p1_name} / {m.pair1.p2_name}</span>
            <span className="text-gray-600"> vs </span>
            <span className={m.winner_pair === 2 ? "text-[#CCFF00] font-semibold" : ""}>{m.pair2.p1_name} / {m.pair2.p2_name}</span>
          </p>
          {setScores && <p className="font-['JetBrains_Mono'] text-[11px] text-gray-500 mt-0.5">{setScores}</p>}
        </div>
        <div className="font-['Barlow_Condensed'] font-black text-2xl text-white flex items-center gap-1 shrink-0">
          <span className={m.winner_pair === 1 ? "text-[#CCFF00]" : ""}>{s1}</span>
          <span className="text-gray-600">-</span>
          <span className={m.winner_pair === 2 ? "text-[#CCFF00]" : ""}>{s2}</span>
        </div>
      </Link>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-home">
      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Dashboard</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">{t("dashboard.hola") || "Hola,"} {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-gray-400 mt-1">{t("dashboard.bienvenido_centro") || "Bienvenido a tu centro de control de padel"}</p>
        <div className="mt-3">
          <OrganizerBadge user={user} variant="tile" />
        </div>
      </div>

      {/* AR 3D Avatar Studio — personalización completa (destacado inmediatamente después del saludo) */}
      <AvatarStudio />

      {/* Admin-controlled hero banner — same for every user */}
      <DashboardHero userName={user?.name} />

      {/* Quick-action grid matching the new mobile & desktop mockups */}
      <DashboardShowcase />

      {/* Padel Center membership banner — only for members */}
      {pc?.is_member && (
        <RouterLink to="/dashboard/padel-center" className="block group" data-testid="dashboard-pc-banner">
          <div className={`card-tactical p-4 border transition-all ${pc.status === "pending" ? "bg-amber-500/10 border-amber-400/40 hover:border-amber-400" : pc.status === "rejected" ? "bg-red-500/10 border-red-400/40 hover:border-red-400" : "bg-gradient-to-r from-[#0a1a0a]/80 via-black to-[#0a1a0a]/40 border-[#CCFF00]/40 hover:border-[#CCFF00]"}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <PadelCenterBadge size={36} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00]">Padel Center</span>
                  {pc.status === "pending" && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30 font-bold animate-pulse">⏳ Pendiente de aprobación</span>
                  )}
                  {pc.status === "rejected" && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30 font-bold">✗ Acceso denegado</span>
                  )}
                  {pc.role_in_pc === "directivo" && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#CCFF00]/15 text-[#CCFF00] border border-[#CCFF00]/30 font-bold">Directivo</span>
                  )}
                  {pc.is_super_admin && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30 font-bold">Super-Admin</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="font-['Barlow_Condensed'] text-2xl font-black text-white uppercase tracking-tight leading-none">
                    {pc.status === "pending" ? "Esperando aprobación del admin"
                      : pc.status === "rejected" ? "Acceso al ecosistema rechazado"
                      : pc.sede === "condado" ? "Sede · Condado del Valle"
                      : pc.sede === "townsquare" ? "Sede · Townsquare"
                      : "Ecosistema Privado"}
                  </span>
                  {pc.status === "approved" && !pc.sede && (
                    <span className="text-[10px] text-amber-300/80 italic">⚠️ Asigna tu sede en Padel Center</span>
                  )}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  {pc.status === "pending"
                    ? "Tu solicitud fue recibida. El admin la revisará pronto."
                    : pc.status === "rejected"
                    ? "Contacta al admin si crees que es un error."
                    : pc.visibility === "padel_center" ? "🔒 Vista privada (solo PC)" : pc.visibility === "global" ? "🌍 Vista global" : "🌐 Vista combinada (Global + PC)"}
                </div>
              </div>
              <ShieldCheck size={28} weight="duotone" className="text-[#CCFF00]/60 group-hover:text-[#CCFF00] transition-colors hidden sm:block" />
            </div>
          </div>
        </RouterLink>
      )}

      {/* Mi Identidad y Equipamiento */}
      <IdentityHero me={me || user} />

      {/* Player KPI Row — Pts Retas · Pts Marcadores · Marcadores · Rank Ligas · Tiempo Jugado · Racha */}
      <PlayerKpiRow />

      {/* Upcoming Bracket Matches (only shows if user has any) */}
      <UpcomingBracketMatches />

      {/* Referral Program — share code, earn DYD */}
      <ReferralCard />

      {/* Vestuario Virtual + Clubes AR — neon overflow hero cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Link to="/dashboard/vestuario-virtual" className="block relative overflow-visible group" data-testid="ar-hero-card">
          <div className="relative card-tactical p-5 sm:p-6 overflow-hidden border-[#00A0FF]/30 hover:border-[#00A0FF]/60 transition-all h-full">
            {/* Neon overflow halos */}
            <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,160,255,0.35) 0%, transparent 70%)" }} aria-hidden />
            <div className="absolute -bottom-20 -right-12 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(204,255,0,0.25) 0%, transparent 70%)" }} aria-hidden />
            <svg className="absolute top-0 right-0 w-32 h-32 opacity-50 pointer-events-none animate-pulse" viewBox="0 0 100 100" fill="none" aria-hidden>
              <path d="M30 10 L25 50 L45 50 L40 90 L70 40 L50 40 L55 10 Z" stroke="#00A0FF" strokeWidth="2" fill="rgba(0,160,255,0.18)" />
            </svg>
            <div className="relative flex items-center gap-4 flex-wrap h-full">
              <div className="shrink-0 w-14 h-14 flex items-center justify-center bg-[#00A0FF]/15 border border-[#00A0FF]/40 rounded">
                <span className="text-2xl">⚡</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#00A0FF] font-bold">Realidad Aumentada</p>
                <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight">Tu Vestuario Virtual</h2>
                <p className="text-xs text-gray-400 mt-0.5">Prueba kits en 360°, ve tu avatar en tu cancha y deja que <span className="text-[#CCFF00] font-semibold">DYD</span> recomiende tu equipo ideal.</p>
              </div>
              <div className="text-right shrink-0">
                <span className="inline-flex items-center gap-1 bg-[#CCFF00] text-black font-bold uppercase text-[11px] tracking-wider px-3 h-9 group-hover:bg-[#B3E600]">
                  Abrir AR →
                </span>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/dashboard/clubes-ar" className="block relative overflow-visible group" data-testid="ar-clubs-hero-card">
          <div className="relative card-tactical p-5 sm:p-6 overflow-hidden border-amber-500/30 hover:border-amber-500/60 transition-all h-full">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,184,0,0.32) 0%, transparent 70%)" }} aria-hidden />
            <div className="absolute -bottom-20 -left-12 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(52,199,89,0.22) 0%, transparent 70%)" }} aria-hidden />
            <svg className="absolute top-1 right-2 w-24 h-24 opacity-60 pointer-events-none" viewBox="0 0 100 100" fill="none" aria-hidden>
              <rect x="10" y="20" width="80" height="40" rx="3" stroke="#FFB800" strokeWidth="2" fill="rgba(255,184,0,0.08)" />
              <rect x="20" y="30" width="25" height="20" stroke="#FFB800" strokeWidth="1.5" fill="rgba(255,184,0,0.15)" />
              <rect x="55" y="30" width="25" height="20" stroke="#FFB800" strokeWidth="1.5" fill="rgba(255,184,0,0.15)" />
            </svg>
            <div className="relative flex items-center gap-4 flex-wrap h-full">
              <div className="shrink-0 w-14 h-14 flex items-center justify-center bg-amber-500/15 border border-amber-500/40 rounded">
                <span className="text-2xl">🏟️</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] tracking-[0.3em] uppercase text-amber-400 font-bold">Clubes en AR</p>
                <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight">Vista 3D de tu Club</h2>
                <p className="text-xs text-gray-400 mt-0.5">Selecciona un club por su logo y véelo en realidad aumentada con todas sus canchas y estado en vivo.</p>
              </div>
              <div className="text-right shrink-0">
                <span className="inline-flex items-center gap-1 bg-amber-400 text-black font-bold uppercase text-[11px] tracking-wider px-3 h-9 group-hover:bg-amber-500">
                  Elegir Club →
                </span>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* 4 scope results + top 4 players per scope (retas, torneos, rentas, ligas) */}
      <ScopeStats />

      {/* My Summary Panel — last 100 (1000 admin) marcadores/torneos/ligas/inscripciones */}
      <MySummaryPanel />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stats-grid">
        {cards.map((card) => (
          <Link to={card.to} key={card.label} className="card-tactical p-6 group hover:border-[#CCFF00]/40 transition-colors cursor-pointer" data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <card.icon size={24} weight="duotone" style={{ color: card.color }} />
              <TrendUp size={16} className="text-gray-600 group-hover:text-[#CCFF00] transition-colors" />
            </div>
            <p className="font-['Barlow_Condensed'] text-3xl font-black text-white">{card.value}</p>
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-500 mt-1">{card.label}</p>
          </Link>
        ))}
      </div>

      {/* Weekly activity chart */}
      <div className="card-tactical p-6" data-testid="weekly-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><ChartBar size={14} /> Actividad</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Ultimos 7 Dias</h2>
          </div>
          <div className="flex gap-3 text-[10px] uppercase tracking-widest text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#CCFF00]" />Partidos</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#FF3B30]" />Retas</span>
          </div>
        </div>
        <div className="h-[220px]" data-testid="weekly-chart-render">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={(stats?.weekly_activity || []).map((d) => ({ ...d, label: WEEK_LABELS[d.weekday] }))} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="label" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#525252" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} labelStyle={{ color: "#CCFF00", textTransform: "uppercase", letterSpacing: "0.1em" }} cursor={{ fill: "rgba(204,255,0,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }} iconType="square" />
              <Bar dataKey="matches" name="Partidos" fill="#CCFF00" radius={[2, 2, 0, 0]} />
              <Bar dataKey="retas" name="Retas" fill="#FF3B30" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-tactical p-6 lg:col-span-2" data-testid="dashboard-live-matches">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Broadcast size={14} /> Marcador</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Partidos en Vivo</h2>
            </div>
            <Link to="/dashboard/marcador" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">Ver todos →</Link>
          </div>
          {liveMatches.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin partidos en vivo</p>
          ) : (
            <div className="space-y-1">{liveMatches.map(renderMatchRow)}</div>
          )}
        </div>

        <div className="card-tactical p-6" data-testid="top-ranking-section">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Ranking</p>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Campeones · Puntos</h2>
          {stats?.top_players?.length > 0 ? (
            <div className="space-y-2">
              {stats.top_players.slice(0, 7).map((p, i) => (
                <div key={i} className={`flex items-center justify-between py-2 border-b border-white/5 last:border-0 ${i === 0 ? "bg-[#CCFF00]/5 -mx-2 px-2 border-l-2 border-l-[#CCFF00]" : ""}`} data-testid={`ranking-player-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {i === 0 ? <Medal size={16} weight="fill" className="text-[#CCFF00] shrink-0" /> : <span className="font-['Barlow_Condensed'] text-lg font-black text-gray-600 w-6">{String(i + 1).padStart(2, "0")}</span>}
                    {p.user_id ? <Link to={`/dashboard/jugador/${p.user_id}`} className={`font-semibold hover:text-[#CCFF00] text-sm truncate ${i === 0 ? "text-[#CCFF00]" : "text-white"}`}>{p.name || "—"}</Link> : <span className="font-semibold text-white text-sm truncate">{p.name || "—"}</span>}
                    {p.category && <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[9px] shrink-0">Cat. {p.category}</Badge>}
                  </div>
                  <span className="font-['JetBrains_Mono'] text-[#CCFF00] font-bold text-sm shrink-0">{p.points || 0}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Sin estadisticas aun</p>
          )}
        </div>
        <div className="card-tactical p-6" data-testid="top-ranking-section">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Ranking Marcadores</p>
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Terminados · Nombre y Puntos</h2>
          {scoreboardRanking.length > 0 ? (
            <div className="space-y-2">
              {scoreboardRanking.map((p, i) => (
                <div key={`${p.name}-${i}`} className={`flex items-center justify-between py-2 border-b border-white/5 last:border-0 ${i === 0 ? "bg-[#CCFF00]/5 -mx-2 px-2 border-l-2 border-l-[#CCFF00]" : ""}`} data-testid={`scoreboard-ranking-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {i === 0 ? <Medal size={16} weight="fill" className="text-[#CCFF00] shrink-0" /> : <span className="font-['Barlow_Condensed'] text-lg font-black text-gray-600 w-6">{String(i + 1).padStart(2, "0")}</span>}
                    {p.user_id ? <Link to={`/dashboard/jugador/${p.user_id}`} className={`font-semibold hover:text-[#CCFF00] text-sm truncate ${i === 0 ? "text-[#CCFF00]" : "text-white"}`}>{p.name}</Link> : <span className={`font-semibold text-sm truncate ${i === 0 ? "text-[#CCFF00]" : "text-gray-300"}`}>{p.name} <span className="text-[9px] text-gray-600 uppercase">invitado</span></span>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[10px] text-gray-500 font-['JetBrains_Mono']">{p.wins}/{p.matches}</span>
                    <span className="font-['JetBrains_Mono'] text-[#CCFF00] font-bold text-sm">{p.points}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Sin marcadores terminados</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-tactical p-6" data-testid="dashboard-validated-matches">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Medal size={14} /> Resultados</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Marcadores Recientes</h2>
            </div>
            <Link to="/dashboard/marcador" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">Historial →</Link>
          </div>
          {recentValidated.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin resultados validados</p>
          ) : (
            <div className="space-y-1">{recentValidated.map(renderMatchRow)}</div>
          )}
        </div>

        <div className="card-tactical p-6" data-testid="dashboard-recent-plays">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-1 flex items-center gap-2"><Sword size={14} /> Jugadas</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Retas Completadas</h2>
            </div>
            <Link to="/dashboard/retas" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">Ver todas →</Link>
          </div>
          {recentRetas.length === 0 ? (
            <p className="text-gray-500 text-sm">Sin retas completadas</p>
          ) : (
            <div className="space-y-1">
              {recentRetas.map((r) => {
                const wonByCh = r.winner_id === r.challenger_id;
                return (
                  <Link to={`/dashboard/retas`} key={r.id} className="flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 px-2 transition-colors" data-testid={`dashboard-reta-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">COMPLETADA</Badge>
                        <span className="text-[10px] text-gray-600 font-['JetBrains_Mono']">{r.date}</span>
                      </div>
                      <p className="text-sm text-white truncate"><span className={wonByCh ? "text-[#CCFF00] font-semibold" : ""}>{r.challenger_name}</span> <span className="text-gray-600">vs</span> <span className={!wonByCh ? "text-[#CCFF00] font-semibold" : ""}>{r.challenged_name}</span></p>
                    </div>
                    {r.score && <span className="font-['JetBrains_Mono'] text-xs text-gray-400 shrink-0">{r.score}</span>}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-tactical p-6" data-testid="upcoming-tournaments">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FFB800] mb-1 flex items-center gap-2"><Trophy size={14} /> Agenda</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Torneos Proximos</h2>
            </div>
            <Link to="/dashboard/torneos" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">Ver →</Link>
          </div>
          {upcomingTournaments.length === 0 ? <p className="text-gray-500 text-sm">Sin torneos proximos</p> : (
            <div className="space-y-2">
              {upcomingTournaments.map((t) => (
                <Link to="/dashboard/torneos" key={t.id} className="block bg-[#0A0A0A] border border-white/10 p-3 hover:border-[#CCFF00]/40 transition-colors" data-testid={`upcoming-tour-${t.id}`}>
                  <p className="text-sm text-white font-semibold truncate">{t.name}</p>
                  <div className="flex gap-2 text-[10px] text-gray-500 mt-1 flex-wrap uppercase tracking-widest">
                    <span><CalendarBlank size={10} className="inline mr-1" />{t.start_date}</span>
                    <span>{t.players?.length || 0}/{t.max_players}</span>
                    {t.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px] py-0">Cat. {t.category}</Badge>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card-tactical p-6" data-testid="upcoming-retas">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF3B30] mb-1 flex items-center gap-2"><Sword size={14} /> Agenda</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Retas Proximas</h2>
            </div>
            <Link to="/dashboard/retas" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">Ver →</Link>
          </div>
          {upcomingRetas.length === 0 ? <p className="text-gray-500 text-sm">Sin retas proximas</p> : (
            <div className="space-y-2">
              {upcomingRetas.map((r) => (
                <Link to="/dashboard/retas" key={r.id} className="block bg-[#0A0A0A] border border-white/10 p-3 hover:border-[#CCFF00]/40 transition-colors" data-testid={`upcoming-reta-${r.id}`}>
                  <p className="text-sm text-white font-semibold truncate">{r.challenger_name} vs {r.challenged_name}</p>
                  <div className="flex gap-2 text-[10px] text-gray-500 mt-1 uppercase tracking-widest">
                    <span><CalendarBlank size={10} className="inline mr-1" />{r.date}{r.time ? ` · ${r.time}` : ""}</span>
                    <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[9px] py-0">{r.status}</Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card-tactical p-6" data-testid="upcoming-rentals">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#34C759] mb-1 flex items-center gap-2"><CalendarCheck size={14} /> Agenda</p>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">Rentas Proximas</h2>
            </div>
            <Link to="/dashboard/canchas" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">Ver →</Link>
          </div>
          {upcomingRentals.length === 0 ? <p className="text-gray-500 text-sm">Sin rentas proximas</p> : (
            <div className="space-y-2">
              {upcomingRentals.map((r) => (
                <Link to="/dashboard/canchas" key={r.id} className="block bg-[#0A0A0A] border border-white/10 p-3 hover:border-[#CCFF00]/40 transition-colors" data-testid={`upcoming-rental-${r.id}`}>
                  <p className="text-sm text-white font-semibold truncate">{r.court_name} · {r.user_name}</p>
                  <div className="flex gap-2 text-[10px] text-gray-500 mt-1 uppercase tracking-widest">
                    <span><CalendarBlank size={10} className="inline mr-1" />{r.date} · {r.time_slot}</span>
                    <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[9px] py-0">{r.status}</Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================
// IdentityHero — user photo + 3D avatar + real gear chips
// =====================================================
function GearChip({ icon: Icon, label, brand, fallback, testId }) {
  const empty = !brand;
  return (
    <div
      className={`flex items-center gap-3 p-3 border transition-colors ${empty ? "border-white/10 bg-[#0A0A0A] opacity-70" : "border-[#CCFF00]/30 bg-[#0A0A0A]"}`}
      data-testid={testId}
    >
      <div
        className="flex items-center justify-center w-10 h-10 shrink-0 border border-white/10"
        style={{ backgroundColor: empty ? "#1A1A1A" : (brand?.color || "#1A1A1A"), color: empty ? "#666" : (brand?.text || "#fff") }}
      >
        <Icon size={20} weight="bold" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[9px] tracking-[0.2em] uppercase font-semibold text-gray-500">{label}</p>
        <p className={`text-sm font-bold truncate ${empty ? "text-gray-600" : "text-white"}`}>
          {brand ? brand.name : fallback}
        </p>
      </div>
    </div>
  );
}

function IdentityHero({ me }) {
  if (!me) return null;
  const pala = getPalaBrand(me.gear_pala_brand);
  const apparel = getApparelBrand(me.gear_apparel_brand);
  const shoes = getShoesBrand(me.gear_shoes_brand);
  const accessories = me.gear_accessories;
  const palaModel = me.gear_pala_model;

  return (
    <div className="card-tactical p-6 relative overflow-hidden" data-testid="identity-hero">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(135deg, #CCFF00 0%, transparent 40%), repeating-linear-gradient(45deg, transparent 0 12px, rgba(255,255,255,0.05) 12px 13px)",
        }}
      />
      <div className="relative grid grid-cols-1 md:grid-cols-[auto_auto_1fr] gap-6 items-center">
        {/* Photo */}
        <div className="flex flex-col items-center gap-2">
          {me.photo_path ? (
            <img
              src={`${API_URL}/api/users/photo/${me.photo_path}`}
              alt="Foto"
              className="w-24 h-24 sm:w-28 sm:h-28 object-cover border-2 border-[#CCFF00]"
              data-testid="identity-photo"
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 border-2 border-dashed border-white/20 flex items-center justify-center text-[10px] uppercase tracking-widest text-gray-600 text-center px-2" data-testid="identity-no-photo">
              Sin foto
            </div>
          )}
          <span className="text-[9px] uppercase tracking-[0.2em] text-gray-500">Foto</span>
        </div>

        {/* Humanoid Avatar with AR glow + brand patches */}
        <div className="flex flex-col items-center gap-2">
          <HumanoidAvatar user={me} size={128} arGlow={true} testId="identity-humanoid" />
          <Link
            to="/dashboard/mi-perfil#avatar"
            className="text-[9px] uppercase tracking-[0.2em] text-[#CCFF00] hover:underline flex items-center gap-1 font-semibold"
            data-testid="identity-edit-avatar-btn"
          >
            <Sparkle size={10} weight="fill" /> Editar Avatar
          </Link>
        </div>

        {/* Gear */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-0.5">Mi Equipamiento</p>
              <p className="text-[11px] text-gray-500">Tu kit oficial de padel</p>
            </div>
            <Link
              to="/dashboard/mi-perfil"
              className="text-[10px] uppercase tracking-wider text-[#CCFF00] hover:underline flex items-center gap-1"
              data-testid="edit-gear-link"
            >
              <Pencil size={11} /> Editar
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <GearChip icon={Trophy} label="Pala" brand={pala} fallback={palaModel || "No definida"} testId="gear-pala" />
            <GearChip icon={TShirt} label="Ropa" brand={apparel} fallback="No definida" testId="gear-apparel" />
            <GearChip icon={Sneaker} label="Calzado" brand={shoes} fallback="No definido" testId="gear-shoes" />
            <GearChip
              icon={Tag}
              label="Accesorios"
              brand={accessories ? { name: accessories, color: "#1A1A1A", text: "#CCFF00" } : null}
              fallback="Ninguno"
              testId="gear-accessories"
            />
          </div>
          {pala && palaModel && (
            <p className="text-[10px] text-gray-500 mt-2 uppercase tracking-widest" data-testid="gear-pala-model">
              Modelo: <span className="text-white">{palaModel}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
